import React, { useState, useMemo, useEffect } from "react";

import DashboardLayout from "@/src/components/layout/DashboardLayout";
import { centreApiService } from "@/src/lib/api-services/centreApiServices";
import CentreNavBar from "@/src/components/locate-us/centreNavbar";
import BannerImage from "@/src/components/locate-us/bannerImage";
import CentreInfo from "@/src/components/locate-us/centreInfo";
import Progressbar from "../../src/components/loading";
import { NEXT_IMAGE_CDN } from "@/src/lib/util/config";

const defaultData = {
    seoDescription:
        "House Of Diagnostics offers an extensive range of diagnostic services like 3T MRI, CT Scan, Whole Body PET-CT, Fully Automated Path Lab for Blood Testing, Bone DEXA, Ultrasound, Digital X-Ray, Echocardiography, tests. Please search the map below to find the centre closest to you.",
    centreLimsName: "Locate Us",
    centreAddress: "",
};

LocateUs.getLayout = (page) => <DashboardLayout>{page}</DashboardLayout>;

export default function LocateUs({ data }) {
    const { mapLocations, facilitiesList } = data || {};
    const defaultBanner = `${NEXT_IMAGE_CDN}/icon/image_section.png`;
    const [centers, setCenters] = useState([]);

    // Convert raw map data into structured map items
    const mapData = useMemo(() => {
        return mapLocations?.map((centre) => ({
            id: centre.urlSlug,
            name: centre.centreName,
            latitude: parseFloat(centre.centreLatitude),
            longitude: parseFloat(centre.centreLongitude),
            address: centre.centreAddress,
            phone: centre.centreMobile,
            email: "",
            hours: centre.openingHours,
            centreType: centre.centreType,
            whatsapp: centre.whatsappUrl,
            googlePlaceId: centre.googlePlaceId,
        }));
    }, [mapLocations]);

    // Flatten facilities list
    const flattenedFacilitiesList = useMemo(() => {
        return facilitiesList?.flatMap((facility) =>
            facility.categories.map((category) => ({
                urlSlug: category.urlSlug,
                categoryName: category.categoryName,
                cardImage: category.cardImage,
                sortOrder: category.sortOrder,
                categoryId: category.categoryId,
                categoryType: category.categoryType,
            }))
        );
    }, [facilitiesList]);

    useEffect(() => {
        if (mapData?.length > 0) {
            setCenters(mapData);
        }
    }, [mapData]);

    if (!mapData) {
        return (
            <div className="flex flex-row items-center justify-center h-screen">
                <Progressbar />
            </div>
        );
    }

    return (
        <div>
            {/* Hide banner on mobile screens and show on medium (md) and above */}
            <BannerImage
                src={defaultBanner}
                alt="Locate Us Banner"
                className="hidden md:block"
            />

            {/* Flex column on mobile and flex row on medium screens and above */}

            <div className="flex flex-col-reverse md:flex-row py-5 max-w-7xl mx-auto">
                {/* CentreInfo will appear before CentreNavBar on mobile due to flex-col-reverse */}
                <div className="md:w-1/4">
                    <CentreNavBar />
                </div>
                <div className="md:w-3/4">
                    <CentreInfo
                        data={{
                            ...defaultData,
                            faclityList: flattenedFacilitiesList,
                        }}
                        mapData={mapData}
                        centers={centers}
                    />
                </div>
            </div>
        </div>
    );
}

export async function getServerSideProps() {
    let mapLocations = null;
    let facilitiesList = null;

    try {
        const mapResponse = await centreApiService.mapData();
        mapLocations = mapResponse?.data || [];

        const facilitiesResponse = await centreApiService.facilitiesList();
        facilitiesList = facilitiesResponse?.data || [];
    } catch (error) {
        console.error("Error fetching data for Locate Us page:", error);
    }

    return {
        props: {
            data: {
                mapLocations,
                facilitiesList,
                seoTitle:
                    "Locate a HOD Centre Near You | Diagnostic Centres | HOD",
                seoDescription:
                    "The HOD Diagnostic Centre offer MRI Scans, CT Scan, Path Lab, PET-CT, XRay, Ultrasound & more. Click to find a HOD centre near you.",
                routerPath: "locate-us",
            },
        },
    };
}
